(() => {
  if (customElements.get('pdp-purchase-options')) return;

  const STATE_EVENT = 'pdp:state-change';
  // Seal updates its hidden input and classes after its own click/change handlers.
  const FOLLOW_UP_DELAYS = [0, 120, 300, 800];

  // The Seal Subscriptions widget is third-party DOM without data-* or js- hooks,
  // so it is the only thing selected here by (the app's own) class names.
  const SEAL = {
    widget: '.sealsubs-container',
    planInput: 'input[name="selling_plan"][data-sls-selling_plan]',
    checkedOption: 'input.sls-option:checked',
    activeOption: '.sls-option-container.sls-active',
    option: '.sls-option-container',
    subscriptionOption: '.sls-option-container[data-selling-plan-group]',
    subscriptionPrice: '.sls-selling-plan-group-price',
    intervalSelect: 'select.sls-select[name="subs_interval"]',
  };

  const controllers = new Map();

  class PurchaseController {
    constructor(sectionId, host) {
      this.sectionId = sectionId;
      this.hosts = new Set();
      this.state = null;
      this.stateKey = '';
      this.timers = [];
      this.ownsSelection = false;
      this.submitPriceTouched = false;

      // Quick view copies the section's inner HTML, so the section element itself may be missing.
      this.root =
        document.getElementById(`MainProduct-${sectionId}`) || host.closest('[data-section]') || document.body;
      this.form = document.getElementById(`product-form-${sectionId}`);
      this.data = this.readData();

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onFormData = this.onFormData.bind(this);
      this.onReady = this.onReady.bind(this);
      this.sync = this.sync.bind(this);
      this.scheduleSync = this.scheduleSync.bind(this);
      this.applySubmitPrice = this.applySubmitPrice.bind(this);

      this.sealObserver = new MutationObserver(this.scheduleSync);
      this.submitPriceObserver = new MutationObserver(this.applySubmitPrice);
      this.observed = new WeakSet();
    }

    readData() {
      const script = document.getElementById(`PdpData-${this.sectionId}`);
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    start() {
      if (!this.data) return;

      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('click', this.onClick);
      this.form?.addEventListener('formdata', this.onFormData);

      this.sync();
      // Components defined by later deferred scripts miss the first event.
      if (document.readyState !== 'complete') {
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      }
    }

    stop() {
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('click', this.onClick);
      this.form?.removeEventListener('formdata', this.onFormData);
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.sealObserver.disconnect();
      this.submitPriceObserver.disconnect();
      this.clearTimers();
    }

    addHost(host) {
      this.hosts.add(host);
      if (this.state) this.renderHost(host);
      this.scheduleSync();
    }

    removeHost(host) {
      this.hosts.delete(host);
      return this.hosts.size === 0;
    }

    onChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const isVariantInput = target.matches('input[name="id"]') && target.form === this.form;
      if (isVariantInput || target.closest(SEAL.widget)) this.scheduleSync();
    }

    onClick(event) {
      if (event.target instanceof Element && event.target.closest(SEAL.widget)) this.scheduleSync();
    }

    // product-form.js submits `new FormData(form)`, which fires this event.
    onFormData(event) {
      if (!this.ownsSelection || !this.state) return;
      if (this.state.sellingPlanId) {
        event.formData.set('selling_plan', this.state.sellingPlanId);
      } else {
        event.formData.delete('selling_plan');
      }
    }

    onReady() {
      if (this.state) this.dispatch();
    }

    clearTimers() {
      this.timers.forEach((timer) => clearTimeout(timer));
      this.timers = [];
    }

    scheduleSync() {
      this.clearTimers();
      this.timers = FOLLOW_UP_DELAYS.map((delay) => setTimeout(this.sync, delay));
    }

    observe() {
      const options = {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-checked', 'class', 'value'],
      };
      [this.root.querySelector('.js-pdp-seal-target'), this.form].forEach((node) => {
        if (!node || this.observed.has(node)) return;
        this.observed.add(node);
        this.sealObserver.observe(node, options);
      });

      const submitPrice = this.submitPrice;
      if (submitPrice && !this.observed.has(submitPrice)) {
        this.observed.add(submitPrice);
        this.submitPriceObserver.observe(submitPrice, { childList: true, characterData: true, subtree: true });
      }
    }

    get submitPrice() {
      return this.form?.querySelector('.js-product-submit-price') || null;
    }

    // The widget in the theme's mount point wins; a widget Seal injected into the form is the fallback.
    findWidget() {
      const own = this.root.querySelector(`.js-pdp-seal-target ${SEAL.widget}`);
      if (own) return { widget: own, owned: true };
      const inForm = this.form?.querySelector(SEAL.widget);
      return inForm ? { widget: inForm, owned: false } : { widget: null, owned: false };
    }

    readSelection(widget) {
      if (!widget) return { subscription: false, planId: '' };

      const planInput = widget.querySelector(SEAL.planInput);
      if (planInput) return { subscription: planInput.value !== '', planId: planInput.value };

      const option = widget.querySelector(SEAL.checkedOption)?.closest(SEAL.option) || widget.querySelector(SEAL.activeOption);
      if (!option || !option.matches(SEAL.subscriptionOption)) return { subscription: false, planId: '' };
      return { subscription: true, planId: option.querySelector(SEAL.intervalSelect)?.value || '' };
    }

    computeState(widget) {
      const variantId = this.form?.querySelector('input[name="id"]')?.value;
      const variant = this.data.variants?.[variantId];
      if (!variant) return null;

      const selection = this.readSelection(widget);
      const plan = selection.planId ? variant.sellingPlans?.[selection.planId] : null;
      const price = plan ? plan.price : variant.price;

      let compareAtPrice = null;
      if (selection.subscription) {
        compareAtPrice = variant.price;
      } else if (variant.compareAtPrice > variant.price) {
        compareAtPrice = variant.compareAtPrice;
      }

      const daysSupply = variant.daysSupply > 0 ? variant.daysSupply : null;

      return {
        sectionId: this.sectionId,
        variantId: Number(variantId),
        available: Boolean(variant.available),
        purchaseType: selection.subscription ? 'subscription' : 'one_time',
        sellingPlanId: selection.planId ? Number(selection.planId) : null,
        price,
        compareAtPrice,
        pricePerDay: daysSupply ? Math.round(price / daysSupply) : null,
        comparePricePerDay: daysSupply && compareAtPrice ? Math.round(compareAtPrice / daysSupply) : null,
        daysSupply,
      };
    }

    sync() {
      if (!this.data) return;
      this.observe();

      const { widget, owned } = this.findWidget();
      const state = this.computeState(widget);
      if (!state) return;

      this.ownsSelection = owned;
      this.state = state;

      this.syncPlanInput();
      this.syncSealComparePrice(widget);
      this.applySubmitPrice();

      const stateKey = JSON.stringify(state);
      if (stateKey === this.stateKey) return;
      this.stateKey = stateKey;

      this.hosts.forEach((host) => this.renderHost(host));
      this.dispatch();
    }

    renderHost(host) {
      host.dataset.purchaseType = this.state.purchaseType;
      if (typeof host.render === 'function') host.render(this.state, this);
    }

    dispatch() {
      this.root.dispatchEvent(new CustomEvent(STATE_EVENT, { bubbles: true, detail: { ...this.state } }));
    }

    formatMoney(cents) {
      if (typeof window.formatMoney !== 'function') return '';
      return window.formatMoney(cents, this.data.moneyFormat);
    }

    // Mirror of Seal's selling plan for a widget that sits outside the product form.
    syncPlanInput() {
      const input = this.root.querySelector('.js-pdp-selling-plan');
      if (!input) return;
      const planId = this.ownsSelection && this.state.sellingPlanId ? String(this.state.sellingPlanId) : '';
      if (input.value !== planId) input.value = planId;
      input.disabled = planId === '';
    }

    // Seal prints only the subscription price; the regular price is added next to it
    // as an element of our own, re-created whenever Seal re-renders the widget.
    syncSealComparePrice(widget) {
      const template = this.root.querySelector('.js-pdp-compare-template');
      if (!widget || !template) return;

      const variant = this.data.variants[this.state.variantId];
      const planIds = Object.keys(variant.sellingPlans || {});

      widget.querySelectorAll(SEAL.subscriptionOption).forEach((option) => {
        const priceBox = option.querySelector(SEAL.subscriptionPrice);
        if (!priceBox) return;

        let planId = option.querySelector(SEAL.intervalSelect)?.value || '';
        if (!variant.sellingPlans?.[planId]) planId = planIds.length === 1 ? planIds[0] : '';
        const plan = variant.sellingPlans?.[planId];
        const label = plan && plan.price < variant.price ? this.formatMoney(variant.price) : '';

        let compare = priceBox.querySelector('.js-pdp-seal-compare');
        if (!label) {
          compare?.remove();
          return;
        }
        if (!compare) {
          compare = template.content.firstElementChild.cloneNode(true);
          priceBox.append(compare);
        }
        if (compare.textContent !== label) compare.textContent = label;
      });
    }

    // global.js and discount-toggle.js also write this element (one-time variant price or
    // the Seal price text). It is left to them until a subscription price has been shown;
    // from then on every foreign write is replaced with the selected option's price.
    applySubmitPrice() {
      const submitPrice = this.submitPrice;
      if (!submitPrice || !this.state) return;
      if (this.state.purchaseType === 'subscription') this.submitPriceTouched = true;
      if (!this.submitPriceTouched) return;

      const label = this.formatMoney(this.state.price);
      if (!label) return;
      if (submitPrice.textContent.trim() !== label) submitPrice.textContent = label;
      if (submitPrice.getAttribute('data-label') !== label) submitPrice.setAttribute('data-label', label);
    }
  }

  function acquire(host) {
    const sectionId = host.dataset.sectionId;
    if (!sectionId) return null;

    let controller = controllers.get(sectionId);
    if (!controller) {
      controller = new PurchaseController(sectionId, host);
      controllers.set(sectionId, controller);
      controller.start();
    }
    controller.addHost(host);
    return controller;
  }

  function release(host, controller) {
    if (!controller) return;
    if (controller.removeHost(host)) {
      controller.stop();
      controllers.delete(controller.sectionId);
    }
  }

  class PdpPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.controller = acquire(this);
    }

    disconnectedCallback() {
      release(this, this.controller);
      this.controller = null;
    }
  }

  class PdpPricePerDay extends HTMLElement {
    connectedCallback() {
      this.controller = acquire(this);
    }

    disconnectedCallback() {
      release(this, this.controller);
      this.controller = null;
    }

    render(state, controller) {
      const value = this.querySelector('.js-pdp-price-per-day-value');
      const compare = this.querySelector('.js-pdp-price-per-day-compare');
      const label = state.pricePerDay === null ? '' : controller.formatMoney(state.pricePerDay);
      const main = this.querySelector('.js-pdp-price-per-day-main');
      // Without a price the benefits alone keep the block visible, and they show only for a subscription.
      const showBenefits = state.purchaseType === 'subscription' && Boolean(this.querySelector('.js-pdp-price-per-day-benefits'));

      this.toggleAttribute('hidden', !label && !showBenefits);
      if (main) main.hidden = !label;
      if (!label) return;
      if (value) value.textContent = label;
      if (!compare) return;

      const showCompare =
        state.purchaseType === 'subscription' &&
        state.comparePricePerDay !== null &&
        state.comparePricePerDay > state.pricePerDay;
      compare.textContent = showCompare ? controller.formatMoney(state.comparePricePerDay) : '';
      compare.hidden = !showCompare;
    }
  }

  customElements.define('pdp-purchase-options', PdpPurchaseOptions);
  customElements.define('pdp-price-per-day', PdpPricePerDay);
})();
